'use client';

import React, { useCallback, useMemo } from 'react';
import type { PromotionalItemCardProps } from './promotional-item-card.types';
import { Text } from '../../atoms/text';
import { cn } from '../../../lib/utils';
import styles from './promotional-item-card.module.css';
import Button from '../../atoms/button';
import { TextIcon } from '../text-icon';
import Icon from '../../atoms/icon';
import Image from '../../atoms/image';

/**
 * PURE PRESENTATION COMPONENT - UI-CORE
 *
 * Generic promotional/bonus item card component.
 * Can be used for gifts, rewards, bonuses, freebies, or any promotional items.
 *
 * This is a BASE UI component that ONLY handles visual display.
 * It has NO knowledge of business logic, Cart, Rules, or Promotions.
 *
 * PRINCIPLES:
 * - Pure presentation - only renders what it receives
 * - No business logic or data transformation
 * - No dependencies on consumer types
 * - Consumer is responsible for preparing display data
 * - Generic naming - not tied to specific business context
 *
 * @example
 * ```tsx
 * // Consumer transforms business data → presentation props
 * const presentationProps = {
 *   title: product.name,
 *   imageSource: product.image,
 *   quantity: product.quantity,
 *   labelText: "Buy 2 Get 1 Free",
 *   priceText: "Free",
 *   onActionPress: () => handleChange(productId),
 * };
 *
 * <PromotionalItemCard {...presentationProps} />
 * ```
 */
export const PromotionalItemCard: React.FC<PromotionalItemCardProps> = ({
    testID = 'promotional-item-card',
    mode = 'compact',
    size = 'small',
    title,
    titleClassName,
    titleWrapperClassName,
    imageSource,
    quantity = 1,
    quantityClassName,
    priceText = 'Miễn phí',
    labelText,
    labelClassName,
    labelIcon,
    badgeText,
    descriptionText,
    actionButtonText = mode === 'compact' ? 'Đổi quà' : 'Xem chi tiết',
    showActionButton = mode === 'compact',
    containerClassName,
    innerContainerClassName,
    containerStyle,
    imageContainerClassName,
    contentClassName,
    onActionPress,
}) => {
    // Format quantity display
    const formattedQuantity = String(quantity).padStart(2, '0');

    // Container class based on mode
    const containerClass = useMemo(
        () => cn(styles.container, mode === 'compact' && styles.containerCompact, styles[size], containerClassName),
        [mode, size, containerClassName]
    );

    // Image container class based on mode
    const imageContainerClass = useMemo(
        () => cn(styles.imageContainer, mode === 'compact' && styles.imageContainerCompact, imageContainerClassName),
        [mode, imageContainerClassName]
    );

    // Title class based on mode
    const titleClass = useMemo(
        () => cn(styles.title, mode === 'compact' && styles.titleCompact, titleClassName),
        [mode, titleClassName]
    );

    // Content class based on mode
    const contentClass = useMemo(
        () => cn(styles.content, contentClassName, mode === 'compact' && styles.contentCompact),
        [mode, contentClassName]
    );

    // Handle action button press
    const handleActionPress = useCallback(() => {
        onActionPress?.();
    }, [onActionPress]);

    // Render quantity badge (compact mode only)
    const renderQuantityBadge = useMemo(
        () => (
            <Text size="small" className={cn(styles.quantity, quantityClassName)}>
                x {formattedQuantity}
            </Text>
        ),
        [formattedQuantity, quantityClassName]
    );

    // Render status badge (for warnings, alerts, etc.)
    const renderStatusBadge = useMemo(() => {
        if (!badgeText) return null;

        return <span className={styles.statusBadge}>{badgeText}</span>;
    }, [badgeText]);

    const renderLabelAction = useMemo(() => {
        const icon = <Icon name="chevron-right" className={styles.chevronIcon} />;
        return (
            <TextIcon
                gap={0}
                containerClassName={styles.labelTextIconContainer}
                rightIcon={icon}
                textClassName={styles.labelAction}
                text={actionButtonText}
            />
        );
    }, [actionButtonText]);

    // Render label with icon (compact mode)
    const renderLabel = useMemo(() => {
        if (mode !== 'compact' || !labelText) return null;

        return (
            <div className={cn(styles.labelContainer)}>
                <div className={styles.label}>
                    {labelIcon}
                    <Text size="small" className={cn(styles.labelText, labelClassName)}>
                        {labelText}
                    </Text>
                </div>
                <Button
                    showIf={!showActionButton && !!onActionPress}
                    type="button"
                    variant="text"
                    className={styles.actionButton}
                    onClick={handleActionPress}
                >
                    {renderLabelAction}
                </Button>
            </div>
        );
    }, [
        mode,
        labelText,
        showActionButton,
        labelIcon,
        labelClassName,
        onActionPress,
        handleActionPress,
        renderLabelAction,
    ]);

    // Render price and action button (detailed mode)
    const renderPriceAction = useMemo(() => {
        if (mode !== 'detailed') return null;

        return (
            <div className={cn(styles.priceActionRow)}>
                <Text weight="bold" size="large" className={styles.price}>
                    {priceText}
                </Text>
                <Button
                    showIf={!showActionButton && !!onActionPress}
                    type="button"
                    className={styles.detailedActionButton}
                    onClick={handleActionPress}
                >
                    {actionButtonText}
                </Button>
            </div>
        );
    }, [mode, priceText, showActionButton, onActionPress, handleActionPress, actionButtonText]);

    return (
        <div data-testid={testID} className={containerClass} style={containerStyle}>
            <div className={cn(styles.baseContainer, innerContainerClassName)}>
                {/* Item Image */}
                <div className={imageContainerClass}>
                    <Image src={imageSource} alt={title} className={styles.image} />
                </div>

                {/* Title Row */}
                <div className={cn(styles.titleRow, titleWrapperClassName)}>
                    {mode === 'compact' && renderStatusBadge}
                    <Text className={titleClass} data-testid="promotional-item-title">
                        {title}
                    </Text>
                </div>

                {renderQuantityBadge}

                {/* Content Area */}
                <div className={contentClass}>
                    {/* Label (Compact Mode) */}
                    {renderLabel}

                    {/* Description Text */}
                    {mode === 'compact' && descriptionText && (
                        <Text size="small" className={styles.description} data-testid="promotional-description">
                            {descriptionText}
                        </Text>
                    )}

                    {/* Price & Action Button (Detailed Mode) */}
                    {renderPriceAction}
                </div>
            </div>
        </div>
    );
};

export default PromotionalItemCard;
